import { ChiroUpJSON } from '@/libs/core/src/lib/functions/ChiroUpJSON';
import { BusinessRuleSeverity } from '@chiroup/core/constants/globals';
import {
  DevtoolsGetBillingPreviewResponse,
  DevtoolsGetPatientResponse,
  EraPanelResponse,
  InvoicePanelResponse,
  TransactionDebugResponse,
} from '@chiroup/core/types/Devtools.type';
import { use, useState } from 'react';
import { ToastContext, ToastTypes } from '../contexts/toast.context';
import devtoolsService from '../services/devtools.service';

const useDevtools = function (feature: string) {
  const [isFetching, setIsFetching] = useState(false);
  const { createToast } = use(ToastContext);

  if (feature === 'transaction-debug') {
    const get = async (val: any) => {
      let res: TransactionDebugResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.transactionDebug.get(val);
        createToast({
          type: ToastTypes.Success,
          title: 'Success!',
          description: 'Check the payload in the console.',
          duration: 5000,
        });
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    return {
      get,
      isFetching,
    };
  } else if (feature === 'invoice-panel') {
    const get = async (val: any) => {
      let res: InvoicePanelResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.invoicePanel.get(val);
        createToast({
          type: ToastTypes.Success,
          title: 'Success!',
          description: 'Check the payload in the console.',
          duration: 5000,
        });
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const move = async (val: any) => {
      let res: InvoicePanelResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.invoicePanel.move(val);
        createToast({
          type: ToastTypes.Success,
          title: 'Success!',
          description: 'Check the payload in the console.',
          duration: 5000,
        });
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    return {
      get,
      isFetching,
      move,
    };
  } else if (feature === 'patient-purge') {
    const getPatient = async (val: any) => {
      let res: DevtoolsGetPatientResponse | null = null;
      const showToast = !!val?.options?.showToast;
      setIsFetching(true);

      try {
        res = await devtoolsService.patientPurge.getPatient(val);

        if (showToast) {
          createToast({
            type: ToastTypes.Success,
            title: 'Success!',
            description: 'Check the payload in the console.',
            duration: 5000,
          });
        }
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const getPreview = async (val: any) => {
      let res: DevtoolsGetBillingPreviewResponse | null = null;
      const showToast = !!val?.options?.showToast;
      setIsFetching(true);

      try {
        res = await devtoolsService.patientPurge.getPreview(val);

        if (showToast) {
          createToast({
            type: ToastTypes.Success,
            title: 'Success!',
            description: 'Check the payload in the console.',
            duration: 5000,
          });
        }
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const purgePreview = async (val: any) => {
      let res: DevtoolsGetBillingPreviewResponse | null = null;
      const showToast = !!val?.options?.showToast;
      setIsFetching(true);

      try {
        res = await devtoolsService.patientPurge.purgePreview(val);

        if (showToast) {
          createToast({
            type: ToastTypes.Success,
            title: 'Success!',
            description: 'Check the payload in the console.',
            duration: 5000,
          });
        }
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const postPurgeActions = async (val: any) => {
      let res: any | null = null;
      const showToast = !!val?.options?.showToast;
      setIsFetching(true);

      try {
        res = await devtoolsService.patientPurge.postPurgeActions(val);

        if (showToast) {
          createToast({
            type: ToastTypes.Success,
            title: 'Success!',
            description: 'Check the payload in the console.',
            duration: 5000,
          });
        }
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    return {
      getPatient,
      getPreview,
      postPurgeActions,
      purgePreview,
      isFetching,
    };
  } else if (feature === 'era-panel') {
    const get = async (val: any) => {
      let res: EraPanelResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.eraPanel.get(val);
        res = ChiroUpJSON.expand(res);
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const preflightClaimResponses = async (val: any) => {
      let res: EraPanelResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.eraPanel.preflightClaimResponses(val);
        res = ChiroUpJSON.expand(res);
      } catch (e: any) {
        console.error(e);
        createToast({
          type: ToastTypes.Fail,
          title: 'Error!',
          description: e?.response?.data?.message || 'An error occurred.',
          duration: 5000,
        });
        res = {
          issues: [
            {
              text: e?.response?.data?.message || 'An error occurred.',
              severity: BusinessRuleSeverity.Error,
            },
          ],
        };
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    const confirmed = async (val: any) => {
      let res: EraPanelResponse | null = null;
      setIsFetching(true);

      try {
        res = await devtoolsService.eraPanel.confirmed(val);
      } catch (e: any) {
        console.error(e);
        res = {
          issues: [
            {
              text: e?.response?.data?.message || 'An error occurred.',
              severity: BusinessRuleSeverity.Error,
            },
          ],
        };
      } finally {
        setIsFetching(false);
      }

      return res;
    };

    return {
      get,
      isFetching,
      confirmed,
      preflightClaimResponses,
    };
  } else if (feature === 'billing-key-panel') {
    const processSingle = async (val: { billingKey?: string; billingKeys?: string[] }) => {
      return await devtoolsService.billingKeyPanel.processSingle(val);
    };

    const getOutdatedInvoices = async (val: { clinicId: number; limit?: number }) => {
      return await devtoolsService.billingKeyPanel.getOutdatedInvoices(val);
    };

    const getOutdatedInvoicesCount = async (val: { clinicId: number }) => {
      return await devtoolsService.billingKeyPanel.getOutdatedInvoicesCount(val);
    };

    const getUnrebuiltFromTable = async (val: { tableName: string; limit?: number }) => {
      return await devtoolsService.billingKeyPanel.getUnrebuiltFromTable(val);
    };

    const getUnrebuiltCountFromTable = async (val: { tableName: string }) => {
      return await devtoolsService.billingKeyPanel.getUnrebuiltCountFromTable(val);
    };

    return {
      isFetching,
      processSingle,
      getOutdatedInvoices,
      getOutdatedInvoicesCount,
      getUnrebuiltFromTable,
      getUnrebuiltCountFromTable,
    };
  } else if (feature === 'automation-panel') {
    const getIntegrations = async (val: { clinicId: number }) => {
      return await devtoolsService.automationPanel.getIntegrations(val);
    };

    const getIntegrationLogs = async (val: {
      integrationId: number;
      level?: string;
      startKey?: Record<string, any>;
      startDate?: number;
      endDate?: number;
    }) => {
      return await devtoolsService.automationPanel.getIntegrationLogs(val);
    };

    return {
      isFetching,
      getIntegrations,
      getIntegrationLogs,
    };
  } else if (feature === 'billing-panel') {
    const getBillingProfileUsage = async (val: { clinicId: number }) => {
      return await devtoolsService.billingPanel.getBillingProfileUsage(val);
    };

    const getActiveBillingProfiles = async (val: { clinicId: number }) => {
      return await devtoolsService.billingPanel.getActiveBillingProfiles(val);
    };

    const previewBillingProfileUpdate = async (val: {
      clinicId: number;
      onlyNulls: boolean;
      sourceBillingProfileId: number | null;
    }) => {
      return await devtoolsService.billingPanel.previewBillingProfileUpdate(val);
    };

    const updateBillingProfileIds = async (val: {
      clinicId: number;
      onlyNulls: boolean;
      sourceBillingProfileId: number | null;
      targetBillingProfileId: number | null;
    }) => {
      return await devtoolsService.billingPanel.updateBillingProfileIds(val);
    };

    return {
      isFetching,
      getBillingProfileUsage,
      getActiveBillingProfiles,
      previewBillingProfileUpdate,
      updateBillingProfileIds,
    };
  }

  return {
    availableFeatures: [
      'transaction-debug',
      'invoice-panel',
      'era-panel',
      'patient-purge',
      'billing-key-panel',
      'billing-panel',
      'automation-panel',
    ],
  };
};

export default useDevtools;
